import type { MindItemSearchResult } from '@kimuno/api-sdk/lib/types/responses/MindItem';
import { Box } from '@mui/material';
import React, { useContext } from 'react';
import { Tab } from 'react-bootstrap';
import InfiniteScroll from 'react-infinite-scroller';
import { useSelector } from 'react-redux';

import {
	getMindItemLoadingState as getMindItemLoadingStateSelector,
	getPaginationSelector,
	getSearchResults as getSearchResultsSelector,
	SearchLoadingStatus,
} from '../../../../reducers/search.selectors';
import { trackingEvents } from '../../../../services/tracking/trackConfig';
import { MasterLayoutContext } from '../../../../state/MasterLayoutProvider';
import { SearchResultsTab } from '../../../../types/search';
import { hasNext } from '../../../../utils/pagination';
import useSearchExperiments from '../../../../utils/useSearchExperiments';
import MindItemMuiCard, {
	type MindItemMuiCardProps,
} from '../../../cards/MuiCard/MindItemMuiCard/MindItemMuiCard';
import VideoMuiCard from '../../../cards/MuiCard/VideoMuiCard/VideoMuiCard';
import WithTracking from '../../../utils/WithTracking';
import RelatedTopicsPills, {
	RELATED_TOPICS_INLINE_INDEX,
} from '../../RelatedTopicsPills/RelatedTopicsPills';
import EmptySearchResultsNewUI from '../EmptySearchResultsNewUI';
import { getCommonCardProps } from '../SearchResults';
import SearchResultsContainer from '../SearchResultsContainer';
import SearchResultsLoader from '../SearchResultsLoader';
import SearchResultsTopbarNewUI from '../SearchResultsTopbar';
import SingleSearchResultContainer from '../SingleSearchResultContainer';
import type { SearchResultsTabProps } from './SearchResultsTab';
import { getVideoMuiCardProps } from './VideoMindItemsTab';

export const getMindItemMuiCardProps = (
	item: MindItemSearchResult,
	useAiFilters?: boolean
): MindItemMuiCardProps => {
	return {
		...getCommonCardProps(item, useAiFilters),
		numberOfPages: item.pageCount || undefined,
		product: item.populatedProduct,
		mindItemPageNumber: item.bestMatch?.mindItemPageNumber,
	};
};

const MindItemsTab = ({
	searchTerm,
	requestFilters,
	loadMore,
	searchEngineVersion,
	onShowNotification,
	onRelatedTopicSelect,
}: SearchResultsTabProps) => {
	const mindItems = useSelector((state) => getSearchResultsSelector(state, 'mindItems'));
	const mindItemsPagination = useSelector(getPaginationSelector('mindItems'));
	const mindItemLoadingState = useSelector(getMindItemLoadingStateSelector);
	const { mainContentContainer } = useContext(MasterLayoutContext);
	const { useAiFilters } = useSearchExperiments();

	return (
		<Tab.Pane eventKey={SearchResultsTab.MINDITEMS} data-testid="new-ui-mindItem-tab">
			<SearchResultsTopbarNewUI
				total={mindItemsPagination.total}
				searchTerm={searchTerm}
				isLoaded={mindItemLoadingState === SearchLoadingStatus.LOADED}
				requestFilters={requestFilters}
			/>
			{mindItemLoadingState === SearchLoadingStatus.INITIAL ||
			mindItemLoadingState === SearchLoadingStatus.NONE ? (
				<SearchResultsLoader />
			) : (
				<>
					{mindItems.length > 0 ? (
						<InfiniteScroll
							loadMore={loadMore}
							hasMore={
								hasNext(mindItemsPagination) && mindItemLoadingState !== SearchLoadingStatus.PAGINATION
							}
							className="search-results__infinite-scroll-box"
							useWindow={false}
							getScrollParent={() => mainContentContainer}
						>
							<SearchResultsContainer>
								{mindItems.map((mindItem, index) => (
									<React.Fragment key={mindItem.id}>
										<SingleSearchResultContainer>
											<WithTracking
												events={{
													onClick: trackingEvents.itemOpen,
													onMiddleClick: trackingEvents.itemOpen,
													onContextMenu: trackingEvents.itemOpen,
												}}
												eventPayload={{
													other: {
														minditem_id: mindItem.id,
														index,
														searchEngineVersion,
													},
												}}
											>
												<Box>
													{mindItem.fileType === 'video' ? (
														<VideoMuiCard
															{...getVideoMuiCardProps(mindItem, useAiFilters)}
															index={index}
															onShowNotification={onShowNotification}
														/>
													) : (
														<MindItemMuiCard
															{...getMindItemMuiCardProps(mindItem, useAiFilters)}
															index={index}
															onShowNotification={onShowNotification}
														/>
													)}
												</Box>
											</WithTracking>
										</SingleSearchResultContainer>
										{index === RELATED_TOPICS_INLINE_INDEX && onRelatedTopicSelect && (
											<RelatedTopicsPills variant="inline" term={searchTerm} onSelect={onRelatedTopicSelect} />
										)}
									</React.Fragment>
								))}
							</SearchResultsContainer>
						</InfiniteScroll>
					) : (
						<SearchResultsContainer>
							<EmptySearchResultsNewUI searchTerm={searchTerm} />
						</SearchResultsContainer>
					)}
					{mindItemLoadingState === SearchLoadingStatus.PAGINATION && <SearchResultsLoader />}
				</>
			)}
		</Tab.Pane>
	);
};

export default MindItemsTab;
